import React, { useEffect, useState } from "react";
import api from "../api";
import { Loader2, Save, RotateCcw, Mail, Send } from "lucide-react";
import { toast } from "../hooks/use-toast";

export default function AdminEmailsTab() {
  const [defaults, setDefaults] = useState(null);
  const [overrides, setOverrides] = useState({});
  const [smsDefaults, setSmsDefaults] = useState({});
  const [smsOverrides, setSmsOverrides] = useState({});
  const [smsFeature, setSmsFeature] = useState(false);
  const [addon, setAddon] = useState(5);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(false);
  const [testing, setTesting] = useState("");

  const loadTemplates = () => {
    setErr(false);
    api.get("/admin/email-templates").then(({ data }) => {
      setDefaults(data.defaults);
      setOverrides(data.overrides || {});
      setSmsDefaults(data.sms_defaults || {});
      setSmsOverrides(data.sms_overrides || {});
      setSmsFeature(!!data.sms_confirmation_feature);
      setAddon(data.sms_confirmation_addon || 5);
    }).catch(() => { setDefaults({}); setErr(true); });
  };
  useEffect(() => { loadTemplates(); }, []);

  const val = (tid, field) => {
    const o = overrides[tid] || {};
    return o[field] !== undefined ? o[field] : (defaults[tid]?.[field] || "");
  };
  const setVal = (tid, field, v) => setOverrides((p) => ({ ...p, [tid]: { ...(p[tid] || {}), [field]: v } }));
  const resetOne = (tid) => setOverrides((p) => { const n = { ...p }; delete n[tid]; return n; });

  const sendTest = async (tid) => {
    setTesting(tid);
    try {
      const { data } = await api.post(`/admin/email-templates/${tid}/test`, {
        subject: val(tid, "subject"), heading: val(tid, "heading"), body: val(tid, "body"),
      });
      toast({ title: "Test email sent", description: `Check your inbox at ${data.to}.` });
    } catch (e) {
      toast({ title: "Couldn't send test", description: e?.response?.data?.detail || "Email may not be configured.", variant: "destructive" });
    } finally { setTesting(""); }
  };

  const smsVal = (tid) => {
    const o = smsOverrides[tid] || {};
    return o.message !== undefined ? o.message : (smsDefaults[tid]?.message || "");
  };
  const setSmsVal = (tid, v) => setSmsOverrides((p) => ({ ...p, [tid]: { message: v } }));
  const resetSms = (tid) => setSmsOverrides((p) => { const n = { ...p }; delete n[tid]; return n; });

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/email-templates", { templates: overrides, sms_templates: smsOverrides, sms_confirmation_feature: smsFeature });
      setOverrides(data.overrides || {});
      setSmsOverrides(data.sms_overrides || {});
      toast({ title: "Templates saved", description: "Your wording is now live on outgoing messages." });
    } catch { toast({ title: "Save failed", variant: "destructive" }); }
    finally { setSaving(false); }
  };

  if (!defaults) return <Loader2 className="animate-spin text-[#2ec9c9]" size={32} />;
  if (err) return (
    <div data-testid="admin-emails-tab" className="max-w-[560px] bg-white rounded-2xl p-6 shadow-[0_8px_24px_rgba(20,30,55,0.06)] text-center">
      <p className="text-[#16243b] font-medium mb-3">Couldn't load email templates.</p>
      <button onClick={loadTemplates} data-testid="email-templates-retry" className="gp-btn-teal rounded-full px-5 py-2 text-sm font-medium">Retry</button>
    </div>
  );

  return (
    <div data-testid="admin-emails-tab" className="max-w-[760px] space-y-5">
      <div className="bg-[#eefafa] border border-[#2ec9c9]/30 rounded-2xl p-4 flex items-start gap-3">
        <Mail size={18} className="text-[#1fb0b0] mt-0.5" />
        <p className="text-sm text-[#16243b]">Edit the subject line and wording of the emails your customers receive. Branding, logo, buttons and ticket PDFs stay automatic. Use placeholders like <code className="text-xs bg-white px-1 rounded">{"{event}"}</code> — they're filled in per email. Leave a field blank to use the default.</p>
      </div>

      {Object.entries(defaults).map(([tid, d]) => {
        const edited = !!overrides[tid];
        return (
          <div key={tid} data-testid={`email-template-${tid}`} className="bg-white rounded-2xl p-5 shadow-[0_8px_24px_rgba(20,30,55,0.06)]">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-semibold text-[#16243b]">{d.label}{edited && <span className="ml-2 text-[10px] font-semibold text-[#1fb0b0] bg-[#eefafa] px-2 py-0.5 rounded-full">customised</span>}</h4>
              {edited && <button onClick={() => resetOne(tid)} data-testid={`email-reset-${tid}`} className="text-xs text-[#8a97a8] hover:text-red-500 flex items-center gap-1"><RotateCcw size={12} /> Reset</button>}
            </div>
            <label className="block text-xs font-medium text-[#8a97a8] mb-1">Subject line</label>
            <input value={val(tid, "subject")} onChange={(e) => setVal(tid, "subject", e.target.value)} data-testid={`email-subject-${tid}`}
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-[#2ec9c9] mb-3" />
            <label className="block text-xs font-medium text-[#8a97a8] mb-1">Heading</label>
            <input value={val(tid, "heading")} onChange={(e) => setVal(tid, "heading", e.target.value)} data-testid={`email-heading-${tid}`}
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-[#2ec9c9] mb-3" />
            <label className="block text-xs font-medium text-[#8a97a8] mb-1">Body text</label>
            <textarea value={val(tid, "body")} onChange={(e) => setVal(tid, "body", e.target.value)} data-testid={`email-body-${tid}`} rows={3}
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-[#2ec9c9] resize-y" />
            <p className="text-[11px] text-[#8a97a8] mt-2">Placeholders: {(d.placeholders || []).map((p) => <code key={p} className="bg-[#f6f8fb] px-1 rounded mr-1">{p}</code>)}</p>
            <button onClick={() => sendTest(tid)} disabled={testing === tid} data-testid={`email-test-${tid}`}
              className="mt-3 text-sm font-medium text-[#1fb0b0] border border-[#2ec9c9] rounded-full px-4 py-1.5 hover:bg-[#eefafa] transition-colors flex items-center gap-2 disabled:opacity-50">
              {testing === tid ? <Loader2 className="animate-spin" size={14} /> : <Send size={14} />} Send me a test
            </button>
          </div>
        );
      })}

      {Object.keys(smsDefaults).length > 0 && (
        <>
          <h3 className="text-sm font-semibold text-[#16243b] pt-2">Text message (SMS)</h3>
          <div className="bg-white rounded-2xl p-5 shadow-[0_8px_24px_rgba(20,30,55,0.06)]" data-testid="sms-confirmation-feature">
            <label className="flex items-start gap-3 cursor-pointer">
              <input type="checkbox" checked={smsFeature} onChange={(e) => setSmsFeature(e.target.checked)} data-testid="sms-feature-toggle" className="mt-1 accent-[#2ec9c9]" />
              <span>
                <span className="font-semibold text-[#16243b]">Offer booking-confirmation SMS as an add-on</span>
                <span className="block text-sm text-[#8a97a8] mt-0.5">When on, organisers can enable an instant "you're booked" text after each purchase. Enabling it raises that organiser's monthly fee by £{Number(addon).toFixed(2)} (e.g. £19.99 → £{(19.99 + Number(addon)).toFixed(2)}).</span>
              </span>
            </label>
          </div>
          {Object.entries(smsDefaults).map(([tid, d]) => {
            const edited = !!smsOverrides[tid];
            return (
              <div key={tid} data-testid={`sms-template-${tid}`} className="bg-white rounded-2xl p-5 shadow-[0_8px_24px_rgba(20,30,55,0.06)]">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-semibold text-[#16243b]">{d.label}{edited && <span className="ml-2 text-[10px] font-semibold text-[#1fb0b0] bg-[#eefafa] px-2 py-0.5 rounded-full">customised</span>}</h4>
                  {edited && <button onClick={() => resetSms(tid)} data-testid={`sms-reset-${tid}`} className="text-xs text-[#8a97a8] hover:text-red-500 flex items-center gap-1"><RotateCcw size={12} /> Reset</button>}
                </div>
                <label className="block text-xs font-medium text-[#8a97a8] mb-1">Message</label>
                <textarea value={smsVal(tid)} onChange={(e) => setSmsVal(tid, e.target.value)} data-testid={`sms-message-${tid}`} rows={2} maxLength={320}
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-[#2ec9c9] resize-y" />
                <p className="text-[11px] text-[#8a97a8] mt-1">{smsVal(tid).length} chars · keep it short (standard SMS = 160 chars). Placeholders: {(d.placeholders || []).map((p) => <code key={p} className="bg-[#f6f8fb] px-1 rounded mr-1">{p}</code>)}</p>
              </div>
            );
          })}
        </>
      )}

      <button onClick={save} disabled={saving} data-testid="email-templates-save"
        className="gp-btn-teal rounded-full px-6 py-3 text-sm font-medium flex items-center gap-2 disabled:opacity-50">
        {saving ? <Loader2 className="animate-spin" size={16} /> : <Save size={16} />} Save templates
      </button>
    </div>
  );
}
